import * as React from "react";
import { ClipboardList, Inbox } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";

import { ContractFamiliesViewModel, ContractFamilyDocumentViewModel } from "../contractFamilyViewModels";
import { ContractFamilyDocumentRow, ContractFamilyMembershipChange } from "./ContractFamilyDocumentRow";
import { ContractFamilyRow } from "./ContractFamilyRow";

interface Props {
  viewModel: ContractFamiliesViewModel;
  selectedDocumentId: string | null;
  onSelectDocument: (document: ContractFamilyDocumentViewModel) => void;
  onReview?: () => void;
  onMembershipChanged?: (change: ContractFamilyMembershipChange) => void;
  pipelineRunId?: string | null;
  onPipelineDocumentExcluded?: () => void;
  readOnly?: boolean;
}

export const ContractFamiliesTable: React.FC<Props> = ({
  viewModel,
  selectedDocumentId,
  onSelectDocument,
  onReview,
  onMembershipChanged,
  pipelineRunId,
  onPipelineDocumentExcluded,
  readOnly = false,
}) => {
  const familyIds = React.useMemo(() => viewModel.families.map(family => family.familyId), [viewModel.families]);
  const [expandedFamilyIds, setExpandedFamilyIds] = React.useState<Set<string>>(
    () => new Set(viewModel.families.length <= 3 ? familyIds : []),
  );
  React.useEffect(() => {
    setExpandedFamilyIds(current => {
      const existing = new Set([...current].filter(id => familyIds.includes(id)));
      if (familyIds.length <= 3) familyIds.forEach(id => existing.add(id));
      return existing;
    });
  }, [familyIds]);
  if (viewModel.families.length === 0 && viewModel.ungroupedDocuments.length === 0) {
    if (viewModel.reviewCount > 0) {
      const documentLabel = viewModel.reviewCount === 1 ? "document needs" : "documents need";

      return (
        <div className="flex flex-col items-start gap-3 px-4 py-10">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-desert-sand-100 p-2 text-desert-sand-700">
              <ClipboardList className="h-4 w-4" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-950">No families created yet</p>
              <p className="mt-1 text-sm text-slate-600">
                {viewModel.reviewCount} {documentLabel} review before they can be added to a family.
              </p>
            </div>
          </div>
          {onReview ? (
            <Button type="button" size="sm" onClick={onReview}>
              Review documents
            </Button>
          ) : null}
        </div>
      );
    }

    return <div className="px-4 py-10 text-sm text-slate-500">No contract families found.</div>;
  }

  return (
    <div>
      <div className="flex justify-end gap-2 border-b px-3 py-2">
        <Button type="button" size="xs" variant="outline" onClick={() => setExpandedFamilyIds(new Set(familyIds))}>
          Expand all
        </Button>
        <Button type="button" size="xs" variant="outline" onClick={() => setExpandedFamilyIds(new Set())}>
          Collapse all
        </Button>
      </div>
      <Table className="min-w-[760px]">
        <TableHeader className="sticky top-0 z-10 bg-white">
          <TableRow className="hover:bg-white">
            <TableHead className="h-10 px-3 text-xs uppercase tracking-wide">Document</TableHead>
            <TableHead className="h-10 px-3 text-xs uppercase tracking-wide">Role</TableHead>
            <TableHead className="h-10 px-3 text-xs uppercase tracking-wide">Status</TableHead>
            <TableHead className="h-10 px-3 text-right text-xs uppercase tracking-wide">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {viewModel.families.map(family => (
            <ContractFamilyRow
              key={family.familyId}
              family={family}
              families={viewModel.families}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={onSelectDocument}
              onMembershipChanged={onMembershipChanged}
              pipelineRunId={pipelineRunId}
              onPipelineDocumentExcluded={onPipelineDocumentExcluded}
              readOnly={readOnly}
              expanded={expandedFamilyIds.has(family.familyId)}
              onToggle={() =>
                setExpandedFamilyIds(current => {
                  const next = new Set(current);
                  if (next.has(family.familyId)) next.delete(family.familyId);
                  else next.add(family.familyId);
                  return next;
                })
              }
            />
          ))}
          {viewModel.ungroupedDocuments.length > 0 && (
            <>
              <TableRow className="h-11 bg-slate-50 hover:bg-slate-50">
                <TableCell colSpan={4} className="px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <Inbox className="h-4 w-4 shrink-0 text-slate-500" />
                      <span className="truncate text-sm font-semibold text-slate-950">Ungrouped</span>
                    </div>
                    <span className="shrink-0 text-xs text-slate-500">
                      {viewModel.ungroupedDocuments.length}{" "}
                      {viewModel.ungroupedDocuments.length === 1 ? "document" : "documents"}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
              {viewModel.ungroupedDocuments.map(document => (
                <ContractFamilyDocumentRow
                  key={document.documentId}
                  document={document}
                  selected={document.documentId === selectedDocumentId}
                  families={viewModel.families}
                  onSelectDocument={onSelectDocument}
                  onMembershipChanged={onMembershipChanged}
                  pipelineRunId={pipelineRunId}
                  onPipelineDocumentExcluded={onPipelineDocumentExcluded}
                  readOnly={readOnly}
                />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
